/**
 * Commercial impact — the sentences and figures the import wizard shows.
 *
 * WHY THIS IS A SEPARATE, PURE MODULE
 * -----------------------------------
 * The wizard's job is to make an operator certain about what pressing
 * APPLY will do. That certainty rests on a handful of small decisions —
 * is this an increase or a decrease, does this line change the renewal,
 * what exactly will happen to the licensed-user count — and those
 * decisions are much easier to trust when they are unit-tested in
 * isolation than when they are buried in JSX.
 *
 * THE RULE EVERY FUNCTION HERE OBSERVES
 * -------------------------------------
 * The amount invoiced today and the amount that recurs at renewal are
 * different numbers and are never shown as one. Ten extra users may be
 * billed GBP 3,000 now and be worth GBP 6,000 a year; the screen shows
 * both, labelled, side by side.
 *
 * Nothing here recomputes the server's arithmetic. The backend owns
 * every figure (`services/commercial_import.py`); these helpers only
 * decide how to PRESENT what it returns, so the screen and the database
 * can never disagree about the money.
 */

/** Rounded to the 2dp commercial figures are expressed in. */
export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const num = (n) => (Number.isFinite(Number(n)) ? Number(n) : 0);

/**
 * A signed movement, ready to render.
 *
 * `direction` drives wording and icon rather than colour alone — a
 * red/green-only signal is invisible to a colour-blind reader, and this
 * is the number the whole screen exists to communicate.
 */
export function movement(before, after) {
  const from = round2(before);
  const to = round2(after);
  const delta = round2(to - from);
  return {
    from,
    to,
    delta,
    direction: delta > 0 ? "increase" : delta < 0 ? "decrease" : "unchanged",
    changed: delta !== 0,
  };
}

/** "+£6,000.00" / "−£1,784.00" / "no change" — sign before the symbol. */
export function formatDelta(delta, formatMoney) {
  const d = round2(delta);
  if (d === 0) return "no change";
  const sign = d > 0 ? "+" : "−";
  return `${sign}${formatMoney(Math.abs(d))}`;
}

/**
 * The commercial impact of one import, split into the buckets an
 * operator has to keep apart.
 */
export function impactSummary(session) {
  const before = session?.before_position || {};
  const after = session?.after_position || {};
  const invoice = session?.invoice_impact || {};
  return {
    arr: movement(before.annual_recurring_value, after.annual_recurring_value),
    users: movement(before.licensed_users, after.licensed_users),
    modules: movement(before.module_count, after.module_count),
    integrations: movement(before.integration_count, after.integration_count),
    services: movement(before.recurring_service_count, after.recurring_service_count),
    invoiceTotal: num(invoice.invoice_total),
    oneTimeAmount: num(invoice.one_time_amount),
    recurringBilledNow: num(invoice.recurring_amount_billed_now),
    annualRecurringImpact: num(invoice.annual_recurring_impact),
    unresolved: num(invoice.unresolved_line_count),
    /**
     * True when the invoice charged one amount and the renewal changes
     * by another — the case the whole feature exists for, and the one
     * the screen must never let pass without comment.
     */
    billedDiffersFromRecurring:
      num(invoice.annual_recurring_impact) !== 0
      && round2(invoice.recurring_amount_billed_now)
         !== round2(invoice.annual_recurring_impact),
  };
}

/**
 * One line, stated as a consequence rather than as data.
 *
 * This is the sentence on the confirmation step. It has to be readable
 * by someone who has never seen the commercial model, so it says what
 * will happen in plain terms and never uses a category code.
 */
export function describeLine(line, formatMoney) {
  const billed = num(line.amount_invoiced);
  const annual = num(line.annual_recurring_value);
  const users = num(line.licensed_user_delta);
  const parts = [];

  if (billed !== 0) parts.push(`Amount invoiced now: ${formatMoney(billed)}`);

  if (line.is_recurring) {
    parts.push(
      annual === 0
        ? "Annual recurring impact: none recorded"
        : `Annual recurring impact: ${formatDelta(annual, formatMoney)}`
    );
    if (users) {
      parts.push(
        `Licensed users: ${users > 0 ? "+" : "−"}${Math.abs(users)}`
      );
    }
    parts.push("Included in next renewal: YES");
  } else {
    parts.push("Annual recurring impact: none");
    parts.push("Included in next renewal: NO");
  }

  if (line.is_prorated) {
    parts.push(
      "This charge covers part of the term only, so the amount invoiced is "
      + "less than the full annual value."
    );
  }
  return parts;
}

/**
 * The five wizard steps, and how far the operator has actually got.
 *
 * Derived from the session rather than from local component state, so a
 * refresh or a second browser tab lands on the same step — a wizard that
 * forgets where it was invites the operator to redo work, and redoing a
 * commercial import is exactly what must not happen.
 */
export const WIZARD_STEPS = [
  { id: "upload", label: "Upload", hint: "Add the invoices you want to record." },
  { id: "interpret", label: "Interpretation", hint: "Check what Pulse read from each document." },
  { id: "classify", label: "Classification", hint: "Confirm what each line means commercially." },
  { id: "impact", label: "Commercial impact", hint: "See the before and after position." },
  { id: "confirm", label: "Confirm", hint: "Apply the reviewed changes." },
];

export function furthestStep(session) {
  if (!session) return "upload";
  const files = session.files || [];
  const lines = session.lines || [];
  if (session.status === "applied") return "confirm";
  if (!files.length) return "upload";
  if (!lines.length && !files.some((f) => f.extraction_status === "extracted"
                                        || f.extraction_status === "needs_review")) {
    return "interpret";
  }
  if (session.status === "previewed") return "confirm";
  if (lines.some((l) => l.commercial_category === "UNKNOWN" || l.needs_review)) {
    return "classify";
  }
  return "impact";
}

/** Lines still standing between the operator and Apply. */
export function outstandingLines(session) {
  return (session?.lines || []).filter(
    (l) => l.commercial_category === "UNKNOWN"
      || (l.needs_review && !l.operator_corrected)
  );
}

/** Documents carrying a duplicate warning the operator has not ruled on. */
export function unresolvedDuplicates(session) {
  return (session?.files || []).filter(
    (f) => f.duplicate_verdict && f.duplicate_verdict !== "NONE"
      && !(f.duplicate_acknowledged && f.duplicate_verdict !== "EXACT_DUPLICATE")
  );
}

/**
 * True when an exact duplicate is present. Distinguished from the softer
 * warnings because it can never be acknowledged away — the document must
 * be removed.
 */
export function hasBlockingDuplicate(session) {
  return (session?.files || []).some((f) => f.duplicate_verdict === "EXACT_DUPLICATE");
}
